// Demo data standing in for two integrations the portal does not have yet.
//
// INVITE stands in for the buyer-invite case lookup (PRD §2.3): the requester's
// Invite New Supplier form supplies the sponsor, their contact, the supplier contact's
// name and email, two optional answers, a need-by date and the requester's notes, and
// whether the requester already has a direct contract with the supplier. SEEDED_CANDIDATES stand in for the D&B Cleanse
// Match call the marketplace app already makes (`/api/onboarding/duns-lookup`,
// ADR-0007): up to three candidates, each with a masked number and a confidence
// band. The full D-U-N-S number never reaches the browser there and never does here.

// The requester's answer to "Do you have an existing direct contract with this
// supplier?" on the case page. A direct contract means the work for that requester runs
// on it, and Science Exchange sends the direct-relationship terms that put it on the
// platform; otherwise the supplier signs the standard supplier agreement. "Not sure"
// never reaches the supplier: the requester hands it to a teammate who answers it.
export type RequesterContract =
  | {
      kind: 'direct';
      /** ISO date the requester's direct contract with the supplier expires. */
      expiresOn: string;
    }
  | { kind: 'standard' };

export interface BuyerInvite {
  sponsor: string;
  sponsorShort: string;
  contact: string;
  raisedOn: string;
  /** ISO date, when the requester gave one. */
  neededBy?: string;
  /** The requester's own words on why they want this supplier, when they gave any. */
  notes?: string;
  /** A file the requester attached to the invite. */
  attachment?: { name: string; detail: string; href: string };
  supplierContact: { name: string; email: string };
  contract: RequesterContract;
}

export const INVITE: BuyerInvite = {
  sponsor: 'Halden Pharmaceuticals',
  sponsorShort: 'Halden',
  contact: 'Sam Ortiz',
  raisedOn: 'Tuesday',
  neededBy: '2026-11-16',
  notes:
    "Hi Priya, this is Sam Ortiz inviting you to join Science Exchange. This is regarding the HX-214 bioanalysis study we discussed (quote attached). We'll need you to join this platform as Halden Pharmaceuticals is now routing our procurement through Science Exchange.",
  attachment: {
    name: 'Quote Q-2026-0412 · HX-214 bioanalysis',
    detail: 'From Northlake Bioanalytics · issued 22 Sep 2026',
    href: '/onboard/quote',
  },
  supplierContact: {
    name: 'Priya Raman',
    email: 'p.raman@northlakebio.com',
  },
  // Stands in for the case's contract answer, like the rest of INVITE. Seed
  // `{ kind: 'standard' }` to walk the standard-agreement-only path.
  contract: { kind: 'direct', expiresOn: '2027-04-30' },
};

// The quote the requester attached: the supplier's own quote back to them, which the
// requester forwards so the supplier recognises the work behind the invite.
export const ATTACHED_QUOTE = {
  number: 'Q-2026-0412',
  issuedOn: '2026-09-22',
  validUntil: '2026-12-21',
  study: 'HX-214 bioanalysis',
  from: 'Northlake Bioanalytics, Inc.',
  to: 'Halden Pharmaceuticals',
  attention: 'Sam Ortiz',
  currency: 'USD',
  lines: [
    {
      description: 'LC-MS/MS method transfer and partial validation',
      quantity: 1,
      unitPrice: 18500,
    },
    { description: 'Sample analysis, plasma, incl. QCs', quantity: 480, unitPrice: 95 },
    { description: 'Incurred sample reanalysis', quantity: 48, unitPrice: 95 },
    { description: 'Bioanalytical report (GLP format)', quantity: 1, unitPrice: 3200 },
  ],
} as const;

export const SEEDED_ENTITY = {
  legalName: 'Northlake Bioanalytics, Inc.',
  incorporationCountry: 'US',
  tradingName: 'Northlake Bio',
  street: '1820 Research Park Blvd, Suite 200',
  city: 'Madison',
  state: 'WI',
  postalCode: '53711',
  country: 'US',
} as const;

export interface DunsCandidate {
  id: string;
  legalName: string;
  address: string;
  maskedDuns: string;
  confidence: 'High confidence' | 'Moderate confidence';
}

export const SEEDED_CANDIDATES: readonly DunsCandidate[] = [
  {
    id: 'c1',
    legalName: 'Northlake Bioanalytics, Inc.',
    address: '1820 Research Park Blvd Ste 200, Madison, WI 53711, US',
    maskedDuns: '••-•••-2288',
    confidence: 'High confidence',
  },
  {
    id: 'c2',
    legalName: 'Northlake Bio-Analytical LLC',
    address: '4415 Northlake Dr, Middleton, WI 53562, US',
    maskedDuns: '••-•••-6103',
    confidence: 'Moderate confidence',
  },
];
